/* The bonus stage: a 2048 board whose tiles are this path's own steps. Two of
 * the same step compose into the step above, which is the thesis of the
 * product played out. Nothing below the event tile is a decision, so the
 * ladder stops there and the top tile never merges.
 *
 * Pure board logic, no DOM and no store — the same move the artifact made.
 */
import { MAXV } from '../data/ladder';

export type Dir = 'up' | 'down' | 'left' | 'right';
export const DIRS: Dir[] = ['left', 'right', 'up', 'down'];

export interface MoveResult {
  board: number[];
  moved: boolean;
  gained: number;
  top: number;
  merges: number[];
  at: number[];
  blocked: boolean;
}

export function gLines(dir: Dir): number[][] {
  const lines: number[][] = [];
  if (dir === 'left' || dir === 'right') {
    for (let r = 0; r < 4; r++) {
      const a: number[] = [];
      for (let c = 0; c < 4; c++) a.push(r * 4 + c);
      if (dir === 'right') a.reverse();
      lines.push(a);
    }
  } else {
    for (let c = 0; c < 4; c++) {
      const b: number[] = [];
      for (let r = 0; r < 4; r++) b.push(r * 4 + c);
      if (dir === 'down') b.reverse();
      lines.push(b);
    }
  }
  return lines;
}

export function gMove(board: number[], dir: Dir): MoveResult {
  const nb = board.slice();
  let moved = false;
  let gained = 0;
  let top = 0;
  const merges: number[] = [];
  const at: number[] = [];
  let blocked = false;

  for (const line of gLines(dir)) {
    const vals: number[] = [];
    const out: number[] = [];
    for (let i = 0; i < 4; i++) if (nb[line[i]]) vals.push(nb[line[i]]);
    for (let i = 0; i < vals.length; i++) {
      if (i + 1 < vals.length && vals[i] === vals[i + 1]) {
        if (vals[i] < MAXV) {
          out.push(vals[i] * 2);
          gained += vals[i] * 2;
          merges.push(vals[i] * 2);
          at.push(line[out.length - 1]);
          i++;
          continue;
        }
        /* the top tile keeps both of itself: an event is where the pipeline ends */
        blocked = true;
      }
      out.push(vals[i]);
    }
    for (let j = 0; j < 4; j++) {
      const v = j < out.length ? out[j] : 0;
      if (nb[line[j]] !== v) moved = true;
      nb[line[j]] = v;
    }
  }
  for (let i = 0; i < 16; i++) if (nb[i] > top) top = nb[i];
  return { board: nb, moved, gained, top, merges, at, blocked };
}

export function gCanMove(board: number[]): boolean {
  return DIRS.some((d) => gMove(board, d).moved);
}

export function gLevel(v: number): number {
  return v ? Math.log2(v) : 0;
}

export function emptyCells(board: number[]): number[] {
  const r: number[] = [];
  for (let i = 0; i < 16; i++) if (!board[i]) r.push(i);
  return r;
}

export function gSpawn(board: number[], rng: () => number = Math.random): number[] {
  const e = emptyCells(board);
  if (!e.length) return board;
  board[e[Math.floor(rng() * e.length)]] = rng() < 0.9 ? 2 : 4;
  return board;
}

export function gNew(rng: () => number = Math.random): number[] {
  const b = new Array<number>(16).fill(0);
  gSpawn(b, rng);
  gSpawn(b, rng);
  return b;
}

export function gPeak(board: number[]): number {
  let m = 0;
  for (let i = 0; i < 16; i++) m = Math.max(m, gLevel(board[i]));
  return m;
}
